import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { toast } from "sonner";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "about", label: "About" },
  { id: "pastors", label: "Pastors" },
  { id: "ministries", label: "Ministries" },
  { id: "times", label: "Service Times" },
  { id: "sermons", label: "Sermons" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const give = () =>
    toast.info("Online giving is coming soon", {
      description: "Until then, you can give in person during any service. Thank you for your generosity!",
    });

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 group"
        >
          <img src="/hpvc-logo.png" alt="Hyde Park Victory Church logo" className="w-10 h-10 object-contain transition-transform duration-300 group-hover:scale-105" />
          <span className="font-display font-semibold uppercase tracking-tight text-lg leading-none">
            Hyde Park <span className="text-[#4169E1]">Victory</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm text-zinc-400 hover:text-white transition-colors duration-300 uppercase tracking-widest"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-give-button"
            onClick={give}
            className="hidden sm:inline-flex items-center bg-[#4169E1] hover:bg-[#3555C0] text-white text-sm font-semibold uppercase tracking-widest px-6 py-3 rounded-sm transition-colors duration-300 hover:-translate-y-0.5"
          >
            Give Online
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-white"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-5">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left font-display uppercase tracking-tight text-2xl text-zinc-300 hover:text-[#4169E1] transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-give-button"
              onClick={give}
              className="mt-2 bg-[#4169E1] text-white text-sm font-semibold uppercase tracking-widest px-6 py-4 rounded-sm"
            >
              Give Online
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
